import { useMemo, useState } from "react";
import clsx from "clsx";
import { FieldRow } from "@/components/ui/Field";
import type { ReactNode } from "react";

export interface ChipMultiSelectProps {
  label?: ReactNode;
  hint?: ReactNode;
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  id?: string;
}

export function ChipMultiSelect({
  label,
  hint,
  options,
  value,
  onChange,
  placeholder,
  emptyText = "Keine Treffer …",
  id,
}: ChipMultiSelectProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const chosen = new Set(value);
    return (q ? options.filter((o) => o.toLowerCase().includes(q)) : options).filter(
      (o) => !chosen.has(o),
    );
  }, [options, query, value]);

  function add(v: string) {
    if (value.includes(v)) return;
    onChange([...value, v]);
    setQuery("");
  }

  function remove(v: string) {
    onChange(value.filter((x) => x !== v));
  }

  return (
    <FieldRow label={label} hint={hint} fieldId={id}>
      <div className="relative">
        <div
          className="flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-separator bg-card px-2 py-1 shadow-sm focus-within:border-accent focus-within:ring-2 focus-within:ring-accent-soft"
          onClick={() => setOpen(true)}
        >
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent"
            >
              {v}
              <button
                type="button"
                aria-label={`${v} entfernen`}
                className="rounded-full p-0.5 text-accent hover:bg-fill-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(v);
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            id={id}
            type="text"
            className="min-w-[120px] flex-1 border-0 bg-transparent p-1 text-sm text-label placeholder:text-label-tertiary focus:outline-none focus:ring-0"
            placeholder={value.length === 0 ? placeholder : ""}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 120)}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !query && value.length > 0) {
                e.preventDefault();
                onChange(value.slice(0, -1));
              }
              if (e.key === "Enter" && filtered[0]) {
                e.preventDefault();
                add(filtered[0]);
              }
              if (e.key === "Escape") setOpen(false);
            }}
          />
        </div>
        {open && (
          <ul
            className={clsx(
              "absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-auto rounded-lg border border-separator bg-card py-1 text-sm shadow-ios-md",
            )}
          >
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-label-tertiary">{emptyText}</li>
            )}
            {filtered.map((opt) => (
              <li key={opt}>
                <button
                  type="button"
                  className="block w-full px-3 py-1.5 text-left text-label hover:bg-fill-tertiary"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(opt);
                  }}
                >
                  {opt}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </FieldRow>
  );
}
